import {
  AfterViewInit,
  Component,
  ElementRef,
  inject,
  OnDestroy,
  signal,
  ViewChild,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { TegelModule } from '@scania/tegel-angular-17';
import { ModalAction } from '../../models/modal-action';
import { ModalConfig } from '../../models/modal-config';
import { ModalService } from '../../services/modal.service';
import { NgTemplateOutlet } from '@angular/common';

@Component({
  selector: 'app-modal',
  imports: [TegelModule, NgTemplateOutlet],
  templateUrl: './modal.html',
})
export class Modal implements AfterViewInit, OnDestroy {
  private readonly modalService = inject(ModalService);
  @ViewChild('modalEl', { read: ElementRef }) modalEl!: ElementRef<HTMLElement>;

  config: ModalConfig = {};
  actions: ModalAction[] = [];
  readonly processingAction = signal<ModalAction | null>(null);
  private sub?: Subscription;
  private resolver?: (v: any) => void;

  private readonly sizes = {
    small: 'sm',
    medium: 'md',
    large: 'lg',
  } as const;

  /**
   * Inicializa a escuta das solicitações de abertura do modal.
   * Initializes the modal opening request subscription.
   */
  ngAfterViewInit(): void {
    this.sub = this.modalService.requests$.subscribe(async (req) => {
      this.config = req.config;
      this.actions = [...(req.config.actions ?? [])];
      this.processingAction.set(null);
      this.resolver = req.resolve;

      const el = this.modalEl?.nativeElement as any;

      if (!el) {
        console.error('[ModalHost] modalEl is undefined (ViewChild not resolved)');
        req.resolve({ ok: false, error: 'modalEl undefined' });
        return;
      }

      el.header = this.config.header ?? '';
      el.size = this.sizes[this.config.size ?? 'medium'];
      el.prevent = !!this.config.preventBackdropClose;
      el.closable = this.config.closable ?? true;

      if (typeof el.showModal === 'function') {
        await el.showModal();
      } else {
        console.error('[ModalHost] showModal() not available (Tegel not registered?)');
      }
    });
  }

  /**
   * Executa uma ação do modal e bloqueia novos comandos durante o processamento.
   * Executes a modal action and blocks new commands while processing.
   *
   * @param action Ação selecionada pelo usuário.
   */
  async onActionClick(action: ModalAction): Promise<void> {
    if (this.processingAction()) {
      return;
    }

    this.processingAction.set(action);

    try {
      const result = await action.onClick?.();

      if (action.dismiss && result !== false) {
        await this.close(action);
      }
    } finally {
      this.processingAction.set(null);
    }
  }

  /**
   * Fecha o modal e resolve a solicitação que realizou sua abertura.
   * Closes the modal and resolves the request that opened it.
   *
   * @param result Resultado opcional retornado pelo modal.
   */
  async close(result?: unknown): Promise<void> {
    const el = this.modalEl.nativeElement as any;
    await el.closeModal();
    this.resolver?.(result);
    this.resolver = undefined;
  }

  /**
   * Encerra a inscrição utilizada pelo modal.
   * Disposes the subscription used by the modal.
   */
  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }
}
